import { cm2mm, inch2cm, mm2cm } from "../insole-web-common/src/engine/parameters.js";
import { EnsureBooleanOrNullish, EnsureNumberOrNullish, EnsureStringOrNullish, IsNotNullish, IsNullish, } from "../util.js";
import { GetCurrentMetadataVersion } from "./metadata-backwards-compatibility.js";
function IsFloat2(value) {
    if (value === null || typeof value !== "object") {
        return false;
    }
    return (value.hasOwnProperty("x") &&
        typeof value.x === "number" &&
        value.hasOwnProperty("y") &&
        typeof value.y === "number");
}
export function IsFloat3(value) {
    return IsFloat2(value) && value.hasOwnProperty("z") && typeof value.z === "number";
}
function IsFloat4(value) {
    return IsFloat3(value) && value.hasOwnProperty("w") && typeof value.w === "number";
}
function GetTemplateInfo(insoleName) {
    var _a;
    const insoleNameStr = (_a = EnsureStringOrNullish(insoleName)) !== null && _a !== void 0 ? _a : "";
    const regexResult = /([LR])-(\d+)-(\d+)-(\d+)-(\d+)/.exec(insoleNameStr);
    if (regexResult === null) {
        return null;
    }
    return {
        side: regexResult[1] === "L" ? 0 /* FootSide.Left */ : 1 /* FootSide.Right */,
        length: Number(regexResult[2]) * mm2cm,
        width: Number(regexResult[3]) * mm2cm,
        thickness: Number(regexResult[4]) * mm2cm,
        archHeight: Number(regexResult[5]) * mm2cm,
        insoleName: insoleNameStr,
    };
}
function CreateValueToEnumMapper(possibleValues) {
    return (value) => {
        if (typeof value !== "number") {
            return null;
        }
        for (const val of possibleValues) {
            if (Math.abs(value - val.value) < 1e-3) {
                return val.type;
            }
        }
        return null;
    };
}
const defaultArchModificationValue = (1 / 8) * inch2cm * cm2mm;
const archModificationValueToEnum = CreateValueToEnumMapper([
    {
        type: "decrease",
        value: -defaultArchModificationValue,
    },
    {
        type: "increase",
        value: defaultArchModificationValue,
    },
]);
const defaultWidthModificationValue = (1 / 8) * inch2cm * cm2mm;
const widthModificationValueToEnum = CreateValueToEnumMapper([
    {
        type: "narrow",
        value: -defaultWidthModificationValue,
    },
    {
        type: "wide",
        value: defaultWidthModificationValue,
    },
    {
        type: "extra-wide",
        value: defaultWidthModificationValue * 2,
    },
]);
function ConvertInsoleParameters(result, metadata) {
    var _a, _b, _c, _d, _e;
    const { insoleParameters: params, insoleName } = metadata;
    const templateInfo = GetTemplateInfo(insoleName);
    if (templateInfo !== null) {
        result.selectedTemplate = templateInfo.insoleName;
    }
    if (IsNullish(params)) {
        return;
    }
    const hpSpecific = ((_a = result.hpSpecific) !== null && _a !== void 0 ? _a : (result.hpSpecific = {}));
    // Arch
    {
        if (params.adjustArchHeight !== 0 || params.adjustArchApexPosition !== 0 || params.archModification !== 0) {
            const absoluteArchHeight = (() => {
                const templateArchHeight = templateInfo === null || templateInfo === void 0 ? void 0 : templateInfo.archHeight;
                let adjustArchHeight = EnsureNumberOrNullish(params.adjustArchHeight);
                if (IsNullish(templateArchHeight) || IsNullish(adjustArchHeight)) {
                    return null;
                }
                // archModification is just a UI state, so we add it to the absolute arch height now
                if (typeof params.archModification === "number") {
                    adjustArchHeight += params.archModification;
                }
                return templateArchHeight + adjustArchHeight * mm2cm;
            })();
            result.arch = {
                height: absoluteArchHeight,
                apexPosition: EnsureNumberOrNullish(params.adjustArchApexPosition),
            };
        }
        hpSpecific.archModification = EnsureNumberOrNullish(params.archModification);
        // Multipoint fitting
        if (params.archMultipointFittingEnabled === true) {
            hpSpecific.archMultipointFitting = {
                numSubdivisionPoints: EnsureNumberOrNullish(params.archMultipointFittingSubdivisionPoints),
                distalPoint: EnsureNumberOrNullish(params.archMultipointFittingDistalPoint),
                proximalPoint: EnsureNumberOrNullish(params.archMultipointFittingProximalPoint),
            };
        }
        // Arch wedge
        {
            if (IsNotNullish(params.archWedge) || IsNotNullish(params.archWedgePatternEnabled)) {
                hpSpecific.archWedge = {
                    height: EnsureNumberOrNullish(params.archWedge),
                    patternEnabled: EnsureBooleanOrNullish(params.archWedgePatternEnabled),
                };
            }
        }
    }
    // Forefoot posting
    if (params.forefootPostingAngle !== 0 ||
        params.forefootPostingBalanceToRearfoot === true ||
        params.forefootPostingMeshEnabled) {
        result.forefootPost = {
            angle: EnsureNumberOrNullish(params.forefootPostingAngle),
            extrinsic: EnsureBooleanOrNullish(params.forefootPostingExtrinsic),
            balanceToRearfoot: EnsureBooleanOrNullish(params.forefootPostingBalanceToRearfoot),
        };
        hpSpecific.advancedForefootPost = {
            meshEnabled: EnsureBooleanOrNullish(params.forefootPostingMeshEnabled),
            hexagonPatternEnabled: EnsureBooleanOrNullish(params.forefootPostingHexagonPatternEnabled),
            lateralEdgeRatio: EnsureNumberOrNullish(params.forefootPostingLateralEdgeRatio),
            medialEdgeRatio: EnsureNumberOrNullish(params.forefootPostingMedialEdgeRatio),
            proximalEdgeRatio: EnsureNumberOrNullish(params.forefootPostingProximalEdgeRatio),
            meshType: (() => {
                switch (params.forefootPostingMeshType) {
                    case "solid":
                    case "hollow":
                        return params.forefootPostingMeshType;
                    default:
                        return null;
                }
            })(),
        };
    }
    // Shell options, dimensions
    {
        result.shell = {
            thickness: EnsureNumberOrNullish(params.shellThickness),
            sideRoundingRadius: EnsureNumberOrNullish(params.sideRoundingRadius),
        };
        if (IsNotNullish(result.shell.sideRoundingRadius)) {
            result.shell.sideRoundingRadius *= mm2cm;
        }
        result.dimensions = {
            length: templateInfo === null
                ? null
                : templateInfo.length * ((_b = EnsureNumberOrNullish(params.lengthWidthLength)) !== null && _b !== void 0 ? _b : 1),
            width: templateInfo === null
                ? null
                : templateInfo.width * ((_c = EnsureNumberOrNullish(params.lengthWidthWidth)) !== null && _c !== void 0 ? _c : 1),
        };
        if (typeof params.widthModification === "number" && IsNotNullish(result.dimensions.width)) {
            result.dimensions.width += mm2cm * params.widthModification;
        }
        hpSpecific.widthModification = EnsureNumberOrNullish(params.widthModification);
    }
    // First ray cutout
    if (params.firstRayCutout === true || params.firstRayCutoutEnabled === true) {
        result.firstRayCutout = {
            angle: EnsureNumberOrNullish(params.firstRayCutoutAngle),
            interspace: EnsureNumberOrNullish(params.firstRayCutoutInterspace),
        };
    }
    // Heel posting
    if (params.heelPostingEnabled === true) {
        result.heelPost = {
            angle: EnsureNumberOrNullish(params.heelPostingAlpha),
            heelLift: EnsureNumberOrNullish(params.heelPostingWidth),
            undercut: EnsureNumberOrNullish(params.heelPostingGamma),
            extrinsic: true,
        };
        hpSpecific.advancedHeelPost = {
            extrinsicPostType: "horseshoe",
            curvature: EnsureNumberOrNullish(params.heelPostingCurvature),
            distalPitch: EnsureNumberOrNullish(params.heelPostingDelta),
        };
    }
    // Region based width
    if (params.regionBasedWidthWidth !== 0) {
        const pointPair = {
            proximalPoint: EnsureNumberOrNullish(params.regionBasedWidthProximalPoint),
            distalPoint: EnsureNumberOrNullish(params.regionBasedWidthDistalPoint),
            lockDistal: EnsureBooleanOrNullish(params.regionBasedWidthLockDistal),
            distalWidth: EnsureNumberOrNullish(params.regionBasedWidthWidth),
            smoothing: EnsureNumberOrNullish(params.regionBasedWidthSmoothing),
        };
        const medialSideEnabled = (_d = EnsureBooleanOrNullish(params.regionBasedWidthMedialSideEnabled)) !== null && _d !== void 0 ? _d : true;
        const lateralSideEnabled = (_e = EnsureBooleanOrNullish(params.regionBasedWidthLateralSideEnabled)) !== null && _e !== void 0 ? _e : true;
        result.regionBasedWidth = {
            medialPoints: medialSideEnabled ? [pointPair] : [],
            lateralPoints: lateralSideEnabled ? [pointPair] : [],
        };
    }
    // Flanges
    if (params.medialFlangeEnabled === true) {
        result.medialFlange = {
            height: EnsureNumberOrNullish(params.medialFlangeHeight),
            width: EnsureNumberOrNullish(params.medialFlangeWidth),
            thickness: EnsureNumberOrNullish(params.medialFlangeThickness),
        };
    }
    if (params.lateralFlangeEnabled === true) {
        result.lateralFlange = {
            height: EnsureNumberOrNullish(params.lateralFlangeHeight),
            width: EnsureNumberOrNullish(params.lateralFlangeWidth),
            thickness: EnsureNumberOrNullish(params.lateralFlangeThickness),
        };
    }
    // Heel seat
    {
        result.heelSeat = {
            height: EnsureNumberOrNullish(params.heelSeatHeight),
        };
        hpSpecific.advancedHeelSeat = {
            falloffEnd: EnsureNumberOrNullish(params.heelSeatFalloffEnd),
        };
    }
    // Heel skive
    if (params.heelSkiveEnabled === true) {
        result.kirbySkive = {
            isMedial: EnsureBooleanOrNullish(params.heelSkiveIsMedial),
            depth: EnsureNumberOrNullish(params.heelSkiveDepth),
            angle: EnsureNumberOrNullish(params.heelSkiveAngle),
            tilt: EnsureNumberOrNullish(params.heelSkiveTilt),
        };
    }
    // Forefoot width
    if (params.forefootWidthEnabled === true) {
        hpSpecific.forefootWidth = {
            mpj1offset: EnsureNumberOrNullish(params.forefootWidthMpj1Offset),
            mpj5offset: EnsureNumberOrNullish(params.forefootWidthMpj5Offset),
        };
    }
    // Cutout
    if (IsNotNullish(params.firstCutoutShape) || IsNotNullish(params.secondCutoutShape)) {
        const cutoutShapes = [];
        for (const prefix of ["first", "second"]) {
            const shape = EnsureStringOrNullish(params[`${prefix}CutoutShape`]);
            if (IsNullish(shape) || shape === "none") {
                continue;
            }
            cutoutShapes.push({
                shape: (() => {
                    switch (shape) {
                        case "ellipse":
                        case "rectangular":
                        case "triangle":
                            return shape;
                        default:
                            return null;
                    }
                })(),
                rotation: EnsureNumberOrNullish(params[`${prefix}CutoutRotation`]),
                referencePointU: EnsureNumberOrNullish(params[`${prefix}CutoutPositionU`]),
                referencePointV: EnsureNumberOrNullish(params[`${prefix}CutoutPositionV`]),
                sizeX: EnsureNumberOrNullish(params[`${prefix}CutoutSizeD1`]),
                sizeY: EnsureNumberOrNullish(params[`${prefix}CutoutSizeD2`]),
            });
        }
        if (cutoutShapes.length !== 0) {
            result.cutout = {
                shapes: cutoutShapes,
            };
        }
    }
    // Domes and wells
    if (IsNotNullish(params.firstDomeShape) ||
        IsNotNullish(params.secondDomeShape) ||
        IsNotNullish(params.thirdDomeShape) ||
        IsNotNullish(params.fourthDomeShape)) {
        const domesShapes = [];
        for (const prefix of ["first", "second", "third", "fourth"]) {
            const shape = EnsureStringOrNullish(params[`${prefix}DomeShape`]);
            if (IsNullish(shape) || shape === "none") {
                continue;
            }
            domesShapes.push({
                referencePointU: EnsureNumberOrNullish(params[`${prefix}DomePositionU`]),
                referencePointV: EnsureNumberOrNullish(params[`${prefix}DomePositionV`]),
                height: EnsureNumberOrNullish(params[`${prefix}DomeHeight`]),
                sizeX: EnsureNumberOrNullish(params[`${prefix}DomeSizeD1`]),
                sizeY: EnsureNumberOrNullish(params[`${prefix}DomeSizeD2`]),
                rotation: EnsureNumberOrNullish(params[`${prefix}DomeRotation`]),
                falloff1: EnsureNumberOrNullish(params[`${prefix}DomeSlopeS1`]),
                falloff2: EnsureNumberOrNullish(params[`${prefix}DomeSlopeS2`]),
            });
        }
        if (domesShapes.length !== 0) {
            result.domesAndWells = {
                shapes: domesShapes,
            };
        }
    }
    // Morton's extension
    if (params.mortonsExtensionEnabled === true || params.reverseMortonsExtensionEnabled === true) {
        const isReverse = params.reverseMortonsExtensionEnabled === true;
        const prefix = isReverse ? "reverseMortonsExtension" : "mortonsExtension";
        result.mortonsExtension = {
            isReverse,
            rigidity: (() => {
                const rigidity = params[`${prefix}Type`];
                switch (rigidity) {
                    case "rigid":
                    case "semi-rigid":
                    case "flexible":
                        return rigidity;
                    default:
                        return null;
                }
            })(),
            widthPercent: EnsureNumberOrNullish(params[`${prefix}WidthPercent`]),
            lengthPercent: EnsureNumberOrNullish(params[`${prefix}LengthPercent`]),
            thickness: EnsureNumberOrNullish(params[`${prefix}Thickness`]),
            medialEdgeAngle: EnsureNumberOrNullish(params[`${prefix}MedialEdgeAngle`]),
            lateralEdgeAngle: EnsureNumberOrNullish(isReverse ? params.reverseMortonsExtensionLateralEdgeAngle : params.mortonsExtensionAngle),
        };
    }
    // Sulcus
    switch (params.sulcusInsoleType) {
        case "sulcus":
        case "full":
            result.sulcus = {
                type: params.sulcusInsoleType,
                shoeWidth: (() => {
                    switch (params.sulcusShoeWidth) {
                        case "regular":
                        case "wide":
                        case "extra-wide":
                        case "narrow":
                            return params.sulcusShoeWidth;
                        default:
                            return null;
                    }
                })(),
                rigidity: (() => {
                    switch (params.sulcusPatternType) {
                        case "rigid":
                        case "semi-rigid":
                        case "flexible":
                            return params.sulcusPatternType;
                        default:
                            return null;
                    }
                })(),
            };
            hpSpecific.advancedSulcus = {
                lengthScaleRatio: EnsureNumberOrNullish(params.sulcusLengthScaleRatio),
                widthScaleRatio: EnsureNumberOrNullish(params.sulcusWidthScaleRatio),
                mpj1DistalExtension: EnsureNumberOrNullish(params.sulcusMpj1DistalExtension),
                mpj5DistalExtension: EnsureNumberOrNullish(params.sulcusMpj5DistalExtension),
            };
            break;
        default:
            break;
    }
}
function ConvertFootScanParameters(result, metadata) {
    var _a;
    const params = metadata.footScanParameters;
    if (IsNullish(params)) {
        return;
    }
    const resultParams = {};
    result.footScanParameters = resultParams;
    switch (params.scanMode) {
        case "semi-weighted":
        case "weighted":
        case "non-weighted":
        case "foambox":
            resultParams.scanMode = params.scanMode;
            break;
        default:
            break;
    }
    switch (params.twistCorrection) {
        case "auto":
        case "force-enabled":
        case "force-disabled":
            resultParams.twistCorrection = params.twistCorrection;
            break;
        default:
            break;
    }
    if (typeof params.upDirection === "string" &&
        typeof params.forwardDirection === "string" &&
        typeof params.rightDirection === "string") {
        for (const axis of ["up", "forward", "right"]) {
            const key = `${axis}Direction`;
            const direction = params[key];
            switch (direction) {
                case "X":
                case "Y":
                case "Z":
                case "NegX":
                case "NegY":
                case "NegZ":
                    resultParams[key] = direction;
                    break;
                default:
                    break;
            }
        }
    }
    if (IsFloat4(params.customSettlingRotation)) {
        // Mirror Z
        resultParams.rotation = {
            x: -params.customSettlingRotation.x,
            y: -params.customSettlingRotation.y,
            z: params.customSettlingRotation.z,
            w: params.customSettlingRotation.w,
        };
    }
    if (IsFloat3(params.customSettlingPosition)) {
        resultParams.positionY = params.customSettlingPosition.y;
    }
    resultParams.landmarkPositions = {};
    const { landmarkPositions } = resultParams;
    for (const landmarkName of ["mpj1", "mpj5", "boh", "mh", "lh", "arch"]) {
        const landmarkType = params[`${landmarkName}Type`];
        if (IsNotNullish(landmarkType)) {
            // If the landmark is invalid or missed, then don't store it
            if (landmarkType === "invalid" || landmarkType === "missed") {
                continue;
            }
        }
        const srcLandmark = params[landmarkName];
        if (!IsFloat3(srcLandmark)) {
            continue;
        }
        // Mirror Z
        landmarkPositions[landmarkName] = { x: srcLandmark.x, y: srcLandmark.y, z: -srcLandmark.z };
    }
    const hpSpecific = ((_a = result.hpSpecific) !== null && _a !== void 0 ? _a : (result.hpSpecific = {}));
    if (IsFloat3(params.pcaAxisLocal)) {
        hpSpecific.footscanMainAxis = { x: params.pcaAxisLocal.x, y: params.pcaAxisLocal.y, z: -params.pcaAxisLocal.z };
    }
    if (IsFloat3(params.farthestPointLocal)) {
        hpSpecific.footscanBackmostPoint = {
            x: params.farthestPointLocal.x,
            y: params.farthestPointLocal.y,
            z: -params.farthestPointLocal.z,
        };
    }
}
function ConvertMetadataForSide(metadata) {
    var _a;
    if (IsNullish(metadata)) {
        return null;
    }
    const result = {
        version: GetCurrentMetadataVersion(),
    };
    ConvertInsoleParameters(result, metadata);
    const exportParams = metadata.exportParameters;
    if (IsNotNullish(exportParams)) {
        if ((typeof exportParams.embossedText === "string" && exportParams.embossedText !== "") ||
            exportParams.embossedLogo === true) {
            result.emboss = {
                embossedText: {
                    text: EnsureStringOrNullish(exportParams.embossedText),
                    fontSize: EnsureNumberOrNullish(exportParams.fontSize),
                },
                embossedLogo: EnsureBooleanOrNullish(exportParams.embossedLogo),
            };
        }
        const offset = IsFloat2(exportParams.embossedTextOffset) ? exportParams.embossedTextOffset : null;
        ((_a = result.hpSpecific) !== null && _a !== void 0 ? _a : (result.hpSpecific = {})).advancedExportParams = {
            embossedFiducial: EnsureBooleanOrNullish(exportParams.embossedFiducial),
            plantarPatternEnabled: EnsureBooleanOrNullish(exportParams.hasPlantarPattern),
            embossedTextOffsetX: EnsureNumberOrNullish(offset === null || offset === void 0 ? void 0 : offset.x),
            embossedTextOffsetY: EnsureNumberOrNullish(offset === null || offset === void 0 ? void 0 : offset.y),
        };
    }
    ConvertFootScanParameters(result, metadata);
    return result;
}
export function ConvertHPMetadata(input) {
    return {
        left: ConvertMetadataForSide(input.leftFootMetadata),
        right: ConvertMetadataForSide(input.rightFootMetadata),
    };
}
//# sourceMappingURL=metadata-hp.js.map